import { RoleData } from '~/api/types/roleData.ts';
import { ColumnDef } from '@tanstack/solid-table';
import { getLocalizedString } from '~/i18n/utils.ts';
import { For, Setter, Show } from 'solid-js';
import { ActionsDropdown } from '~/components/ActionsDropdown';
import { ConfirmModalValues } from '~/components/ui/Modal/ConfirmModalContext.tsx';
import { QueryClient } from '@tanstack/solid-query';
import { ToastContextValue } from '~/components/ui/Toast';
import {
  ApiError,
  ApiErrorInstance,
  deleteEnvironmentRole,
  deleteOrganizationRole,
  getEnvironmentRolesQueryKey,
  getUsersQueryKey,
  withHandleError
} from '~/api';
import { HttpStatusCode } from 'axios';
import { getTranslationKeyFunction } from '~/utils/commonUtils.ts';
import { Tag } from '~/components/ui/Tag';
import { PermissionsData } from '~/api/types/permissionData.ts';

enum AuthorizationTabTypes {
  ROLES = 'roles',
  PERMISSIONS = 'permissions'
}

const TRANSLATION_BASE_PATH = `pages.authorization`;
const ROLES_TRANSLATION_BASE_PATH = `${TRANSLATION_BASE_PATH}.roles`;
const PERMISSIONS_TRANSLATION_BASE_PATH = `${TRANSLATION_BASE_PATH}.permissions`;

const getTranslationKey = (translationStr: string): string =>
  `${TRANSLATION_BASE_PATH}.${translationStr}`;

const getRolesTranslationKey = (translationStr: string): string =>
  `${ROLES_TRANSLATION_BASE_PATH}.${translationStr}`;

const getPermissionsTranslationKey = getTranslationKeyFunction(
  PERMISSIONS_TRANSLATION_BASE_PATH
);

const getTooltipContentForDeleteRole = (intl: any, role: RoleData) => {
  if (!role?.defaultMember && !role?.defaultCreator) {
    return '';
  }
  const translationKey =
    role.defaultCreator && role.defaultMember
      ? 'tooltip_delete_default_creator_and_member_role'
      : role.defaultCreator
        ? 'tooltip_delete_default_creator_role'
        : 'tooltip_delete_default_member_role';

  return getLocalizedString(getRolesTranslationKey(translationKey), {
    intl
  });
};

const getRoleListColumnDef = ({
  intl,
  hideActions,
  onEditRole,
  onDeleteRole
}: {
  intl: any;
  hideActions: boolean;
  onEditRole?: (e: RoleData) => void;
  onDeleteRole?: (e: RoleData) => void;
}): ColumnDef<RoleData>[] => [
  {
    header: getLocalizedString(
      getRolesTranslationKey('role_description_table_header'),
      {
        intl
      }
    ),
    accessorKey: 'displayName',
    cell: (info) => {
      const role: RoleData = info.row.original;
      return (
        <div class={`flex flex-col gap-0.5 min-h-[42px] justify-center`}>
          <div class={`flex gap-1`}>
            <span class={`font-medium`}>{role.displayName}</span>
            <Show when={role?.parentRoleDisplayName}>
              <Tag class={`bg-[#F3F3F4] text-black mx-0 font-medium`}>
                <span>
                  {getLocalizedString(getRolesTranslationKey('parent_role'), {
                    parentRole: role.parentRoleDisplayName
                  })}
                </span>
              </Tag>
            </Show>
          </div>
          <span class={`break-words text-fg-muted`}>{role.description}</span>
        </div>
      );
    },
    meta: {
      class: 'w-4/12' // additional meta to control column width
    }
  },
  {
    header: getLocalizedString(
      getRolesTranslationKey('role_name_table_header'),
      { intl }
    ),
    accessorKey: 'name',
    cell: (info) => (
      <span class={`font-code font-medium break-all`}>
        {info.getValue() as string}
      </span>
    ),
    meta: {
      class: 'w-2/12' // additional meta to control column width
    }
  },
  {
    header: getLocalizedString(
      getRolesTranslationKey('role_permissions_table_header'),
      { intl }
    ),
    accessorKey: 'permissions',
    cell: (info) => {
      const permissions = info.getValue() as PermissionsData[];
      return (
        <>
          <Show
            when={!!permissions.length}
            fallback={<span>-</span>}
          >
            <div class={`flex flex-wrap gap-x-1.5 gap-y-3`}>
              <For each={permissions}>
                {(permission) => (
                  <Tag
                    class={`bg-[#EAF0F7] text-[var(--colors-slate-light-11)] mx-0 font-medium`}
                  >
                    {permission.name}
                  </Tag>
                )}
              </For>
            </div>
          </Show>
        </>
      );
    },
    meta: {
      class: 'w-5/12' // additional meta to control column width
    }
  },
  {
    header: '',
    id: 'actions',
    accessorKey: 'id',
    cell: (info) => {
      const role: RoleData = info.row.original;
      return (
        <div
          class={`flex justify-end`}
          onClick={(e) => {
            // Done to ensure that the click on the
            // menu icon and the menu options
            // does not trigger a navigation
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <ActionsDropdown
            id={`${info.getValue()}-action`}
            items={[
              {
                label: getLocalizedString(
                  getRolesTranslationKey('table_action_edit_role')
                ),
                onClick: () => onEditRole?.(role),
                dataTestId: 'authorization-edit-role-modal'
              },
              {
                label: (
                  <span
                    classList={{
                      'text-fg-disabled cursor-not-allowed':
                        role?.defaultMember || role?.defaultCreator,
                      'text-error':
                        !role?.defaultMember && !role?.defaultCreator
                    }}
                  >
                    {getLocalizedString(
                      getRolesTranslationKey('table_action_delete_role')
                    )}
                  </span>
                ),
                onClick: () => onDeleteRole?.(role),
                disabled: role?.defaultMember || role?.defaultCreator,
                tooltipContent: getTooltipContentForDeleteRole(intl, role),
                dataTestId: 'authorization-delete-role-modal'
              }
            ]}
          />
        </div>
      );
    },
    meta: {
      hidden: hideActions,
      class: 'w-1/12' // additional meta to control column width
    }
  }
];

const getRoleNameFromDisplayName = (displayName: string): string => {
  return !displayName
    ? ''
    : displayName.trim().replace(/\s+/gi, '_').toLowerCase();
};

const deleteRoleWithConfirmation = async ({
  intl,
  confirm,
  role,
  orgId,
  refetchOrgRolesData,
  userCount,
  showDeleteModal,
  queryClient,
  toaster
}: {
  intl: any;
  confirm: ConfirmModalValues;
  role: RoleData;
  orgId?: string;
  refetchOrgRolesData?: () => Promise<any>;
  userCount: number;
  showDeleteModal: Setter<boolean>;
  queryClient: QueryClient;
  toaster: ToastContextValue;
}): Promise<void> => {
  confirm.show({
    title: getLocalizedString(
      getRolesTranslationKey('modal_title_delete_role'),
      {
        intl
      }
    ),
    description: getLocalizedString(
      getRolesTranslationKey('confirm_delete_roles_message'),
      {
        intl,
        roleName: role.displayName
      }
    ),
    primaryActionLabel: getLocalizedString(
      getRolesTranslationKey('confirm_delete_roles_cta_label'),
      {
        intl
      }
    ),
    primaryActionDataTestId: 'authorization-role-delete',
    onConfirm: async () => {
      try {
        if (userCount > 0) {
          showDeleteModal(true);
        } else {
          orgId
            ? await deleteOrganizationRole({
                roleName: role.name,
                orgId
              })
            : await deleteEnvironmentRole({
                roleName: role.name
              });
          if (orgId && refetchOrgRolesData) {
            await refetchOrgRolesData();
            await queryClient.invalidateQueries({
              queryKey: getUsersQueryKey(orgId),
              type: 'all',
              exact: true
            });
          } else {
            await queryClient.invalidateQueries({
              queryKey: getEnvironmentRolesQueryKey(),
              type: 'all',
              exact: true
            });
          }
          confirm.setIsLoading(false);
          confirm.close();
          toaster.toast().success({
            description: getLocalizedString(
              getRolesTranslationKey('role_delete_success_toast'),
              {
                intl,
                roleName: role.displayName
              }
            )
          });
        }
      } catch (error) {
        confirm.setIsLoading(false);
        confirm.close();
        toaster.toast().error({
          description: handleDeleteRoleErrors({
            error: error as ApiError | Error,
            intl
          })
        });
      }
    }
  });
};

const errorTranslationBasePath = 'server_errors.roles';

const handleAddRoleErrors = withHandleError(({ error, intl }) => {
  const serverError = error as ApiErrorInstance;
  if (serverError.httpStatus === HttpStatusCode.BadRequest) {
    return getLocalizedString(`${errorTranslationBasePath}.create_failed`, {
      intl
    });
  }
});

const handleEditRoleErrors = withHandleError(({ error, intl }) => {
  const serverError = error as ApiErrorInstance;
  if (serverError.httpStatus === HttpStatusCode.BadRequest) {
    return getLocalizedString(`${errorTranslationBasePath}.update_failed`, {
      intl
    });
  }
});

const handleDeleteRoleErrors = withHandleError(({ error, intl }) => {
  const serverError = error as ApiErrorInstance;
  if (serverError.httpStatus === HttpStatusCode.BadRequest) {
    return getLocalizedString(`${errorTranslationBasePath}.delete_failed`, {
      intl
    });
  }
});

const handleUpdateDefaultRoleErrors = withHandleError(({ error, intl }) => {
  const serverError = error as ApiErrorInstance;
  if (serverError.httpStatus === HttpStatusCode.BadRequest) {
    return getLocalizedString(
      `${errorTranslationBasePath}.default_role_update_failed`,
      {
        intl
      }
    );
  }
});

const getAppTitle = ({
  intl,
  tabType
}: {
  intl: any;
  tabType: AuthorizationTabTypes;
}) => {
  const titleMap: Record<AuthorizationTabTypes, string> = {
    [AuthorizationTabTypes.ROLES]: getLocalizedString('page_titles.roles', {
      intl
    }),
    [AuthorizationTabTypes.PERMISSIONS]: getLocalizedString(
      'page_titles.permissions',
      { intl }
    )
  };

  return (
    (tabType && titleMap?.[tabType]) ||
    getLocalizedString('page_titles.authorization', { intl })
  );
};

export {
  AuthorizationTabTypes,
  getTranslationKey,
  getRolesTranslationKey,
  getPermissionsTranslationKey,
  getRoleListColumnDef,
  getRoleNameFromDisplayName,
  deleteRoleWithConfirmation,
  handleAddRoleErrors,
  handleEditRoleErrors,
  handleDeleteRoleErrors,
  handleUpdateDefaultRoleErrors,
  getAppTitle
};
